import type { QueryKey } from '@tanstack/react-query'
import { getQueryCacheRoot } from '@/lib/queryCache'

export interface ConnectivityResource {
  id: string
  label: string
}

/** Map query roots (and aliases) to a logical UI resource. */
const RESOURCE_ID_BY_ROOT: Record<string, string> = {
  queue: 'queue',
  queueConsultations: 'queue',
  queueExaminations: 'queue',
  queueVisualAcuities: 'queue',
  'queue-admission-availability': 'queue',
  visits: 'queue',
  visit: 'queue',

  patients: 'patients',
  patient: 'patients',
  patientVisits: 'patients',
  patientConsultations: 'patients',

  appointments: 'appointments',

  consultation: 'consultation',
  consultationDraftCache: 'consultation',
  examination: 'consultation',
  visualAcuity: 'consultation',
  prescription: 'consultation',

  dashboard: 'dashboard',
  notifications: 'notifications',
  transactions: 'finance',
  invoices: 'finance',
  reports: 'reports',
  finance: 'finance',
  dispensary: 'dispensary',

  staff: 'staff',
  clinicians: 'staff',
  'staff-schedule': 'staff',

  drugs: 'dispensary',
  products: 'products',
  surgery: 'surgery',
  'surgery-types': 'surgery',
  'clinic-info': 'info',
  'clinic-config': 'config',
  backups: 'backups',
  users: 'users',
  'change-requests': 'change-requests',
  'job-listings': 'jobs',
  jobs: 'jobs',
  chat: 'chat',
}

const RESOURCE_LABEL_BY_ID: Record<string, string> = {
  queue: 'queue',
  patients: 'patients',
  appointments: 'appointments',
  consultation: 'consultation',
  dashboard: 'dashboard',
  notifications: 'notifications',
  finance: 'finance',
  reports: 'reports',
  dispensary: 'dispensary',
  staff: 'staff',
  products: 'products',
  surgery: 'surgery',
  info: 'clinic information',
  config: 'settings',
  backups: 'backups',
  users: 'users',
  'change-requests': 'change requests',
  jobs: 'job listings',
  chat: 'chat',
  app: 'data',
}

export function resolveConnectivityResource(queryKey: QueryKey): ConnectivityResource | null {
  const root = getQueryCacheRoot(queryKey)
  if (!root) return null

  const id = RESOURCE_ID_BY_ROOT[root] ?? root
  const label = RESOURCE_LABEL_BY_ID[id] ?? root.replace(/-/g, ' ')
  return { id, label }
}

export function resolveConnectivityResourceFromMutationKey(
  mutationKey: QueryKey | undefined
): ConnectivityResource | null {
  if (!mutationKey?.length) return null
  return resolveConnectivityResource(mutationKey)
}
